import React from 'react'
import styled from 'styled-components'
import Header, { Title, SubTitle } from '../Header'
import { AttributeList } from '../Attribute'
import TextAttribute from './TextAttribute'
import Section from '../Section'
import Separator from '../Separator'
import ColorAttribute from '../ColorAttribute'
import { capitalize } from 'utils'
import {
  ColorFormat,
  Style,
  ColorVariable,
  TextAlignment,
  TextAttributes,
  TextAttributedString,
  TextProps,
  TextStyle,
  Color as ColorType,
} from 'modules/shares/types'
import { roundUpTo } from 'utils/math'
import { renderLineHeight } from './utils'
import { copySection, copyAlignment } from './copy'
import { Truncate } from 'components/Truncate'
import FullCopyAttribute from '../FullCopyAttribute'
import theme from 'styles/theme'
import { getDirtyAttributes } from 'modules/shares/Inspector/Sidebar/components/Style/DirtyIconTooltip'

// Text Align Images
import alignCenter from 'icons/inspector-text-align-center.svg'
import alignJustify from 'icons/inspector-text-align-justify.svg'
import alignLeft from 'icons/inspector-text-align-left.svg'
import alignMiddle from 'icons/inspector-text-align-middle.svg'
import alignTop from 'icons/inspector-text-align-top.svg'

const TextAlignImg = styled.img`
  width: 17px;
  height: 17px;
  margin-right: 4px;
  opacity: 50%;
`

const TextAlignContainer = styled.div`
  display: flex;
  align-items: center;
`

const TextAlign = styled.span`
  text-transform: capitalize;
`

const TextAlignValue = ({
  alignment,
  label,
}: {
  alignment: TextAlignment
  label: string
}) => {
  const renderImage = () => {
    switch (alignment) {
      case TextAlignment.Bottom:
        return (
          <TextAlignImg
            src={alignTop}
            style={{ transform: 'rotate(180deg)' }}
          />
        )
      case TextAlignment.Top:
        return <TextAlignImg src={alignTop} />
      case TextAlignment.Right:
        return (
          <TextAlignImg src={alignLeft} style={{ transform: 'scaleX(-1)' }} />
        )
      case TextAlignment.Left:
        return <TextAlignImg src={alignLeft} />
      case TextAlignment.Middle:
        return <TextAlignImg src={alignMiddle} />
      case TextAlignment.Center:
        return <TextAlignImg src={alignCenter} />
      case TextAlignment.Justified:
        return <TextAlignImg src={alignJustify} />
      default:
        return null
    }
  }
  return (
    <TextAlignContainer>
      {renderImage()}
      <TextAlign
        aria-label={`${label} ${
          alignment === TextAlignment.Justified ? 'justify' : alignment
        }`}>
        {alignment === TextAlignment.Justified ? 'justify' : alignment}
      </TextAlign>
    </TextAlignContainer>
  )
}

const renderTextAttribute = (
  {
    font,
    color,
    letterSpacing,
    paragraphStyle,
    verticalAlignment,
    text,
    onColorFormatChange,
    colorFormat,
    colorVariables,
    originalSharedStyleValues,
  }: TextAttributes & {
    text: string
    originalSharedStyleValues?: TextStyle
  } & ColorFormatModifier & {
      colorVariables: { [key: string]: ColorVariable }
    },
  i: number
) => {
  const { family, weight, weightName, size, style } = font || {}
  const displayedStyle = style && style !== 'normal' ? style : ''
  const { spacing, maxLineHeight, alignment } = paragraphStyle || {}

  const alignmentCopyValue = copyAlignment({
    verticalAlignment,
    paragraphStyle,
  })

  const sectionCopyValue = copySection({
    font,
    color,
    colorFormat,
    letterSpacing,
    paragraphStyle,
    verticalAlignment,
  })

  const textOriginalStyles = originalSharedStyleValues?.text

  const dirtyHeaderAttributes =
    textOriginalStyles === null
      ? { originalValue: 'disabled', originalProperty: 'text style' }
      : undefined

  return (
    <div key={`TextStyle${i}`}>
      {' '}
      <Separator />{' '}
      <Section data-testid="inspector-sidebar-text">
        <Header
          copyValue={sectionCopyValue}
          dirtyAttributes={dirtyHeaderAttributes}>
          <Title>
            <Truncate
              width={
                theme.sidebar.rightWidth - 60
              }>{`Text: "${text}"`}</Truncate>
          </Title>
        </Header>
        <AttributeList>
          {family ? (
            <TextAttribute
              value={family}
              label="Typeface"
              dirtyAttributes={getDirtyAttributes({
                originalValueKey: 'family',
                valueToDisplay: textOriginalStyles?.font?.family ?? '',
                originalValues: textOriginalStyles?.font,
                labelToDisplay: 'Font Family',
              })}
            />
          ) : null}
          {weight && weightName ? (
            <TextAttribute
              value={`${capitalize(weightName)} ${capitalize(displayedStyle)} ${
                weightName !== weight.toString() ? `(${weight})` : ''
              }`}
              label="Weight"
              copyValue={weight}
              dirtyAttributes={getDirtyAttributes({
                originalValueKey: 'weight',
                valueToDisplay:
                  textOriginalStyles?.font?.weight?.toString() ?? '',
                originalValues: textOriginalStyles?.font,
              })}
            />
          ) : null}
          {size || size === 0 ? (
            <TextAttribute
              value={`${roundUpTo(size, 2)}px`}
              label="Size"
              copyValue={`${size}`}
              dirtyAttributes={getDirtyAttributes({
                originalValueKey: 'size',
                valueToDisplay: `${roundUpTo(
                  textOriginalStyles?.font?.size ?? 0,
                  2
                )}`,
                originalValues: textOriginalStyles?.font,
              })}
            />
          ) : null}
          {letterSpacing || letterSpacing === 0 ? (
            <TextAttribute
              value={`${roundUpTo(letterSpacing, 2)}`}
              label="Character"
              copyValue={`${letterSpacing}`}
              dirtyAttributes={getDirtyAttributes({
                originalValueKey: 'letterSpacing',
                valueToDisplay: `${roundUpTo(
                  textOriginalStyles?.letterSpacing ?? 0,
                  2
                )}`,
                originalValues: textOriginalStyles,
                labelToDisplay: 'Character',
              })}
            />
          ) : null}
          {maxLineHeight ? (
            <TextAttribute
              value={`${renderLineHeight(
                maxLineHeight,
                size ? maxLineHeight / size : undefined
              )}`}
              label="Line Height"
              copyValue={`${maxLineHeight}`}
              dirtyAttributes={getDirtyAttributes({
                originalValueKey: 'maxLineHeight',
                valueToDisplay: `${roundUpTo(
                  textOriginalStyles?.paragraphStyle?.maxLineHeight ?? 0,
                  2
                )}`,
                originalValues: textOriginalStyles?.paragraphStyle,
                labelToDisplay: 'Line Height',
              })}
            />
          ) : null}
          {spacing ? (
            <TextAttribute
              value={`${roundUpTo(spacing, 2)}`}
              label="Paragraph"
              copyValue={`${spacing}`}
              dirtyAttributes={getDirtyAttributes({
                originalValueKey: 'spacing',
                valueToDisplay: `${roundUpTo(
                  textOriginalStyles?.paragraphStyle?.spacing ?? 0,
                  2
                )}`,
                originalValues: textOriginalStyles?.paragraphStyle,
                labelToDisplay: 'Paragraph',
              })}
            />
          ) : null}
          {color ? (
            <ColorAttribute
              red={color.red}
              green={color.green}
              blue={color.blue}
              alpha={color.alpha}
              colorVariableID={color.colorVariableID}
              colorVariables={colorVariables}
              onColorFormatChange={onColorFormatChange}
              colorFormat={colorFormat}
              dirtyAttributes={
                textOriginalStyles?.color
                  ? {
                      originalValue: textOriginalStyles.color as ColorType,
                      originalProperty: 'Color',
                    }
                  : undefined
              }
            />
          ) : null}
        </AttributeList>
        {alignment || verticalAlignment ? (
          <>
            <Separator />
            <Header copyValue={alignmentCopyValue}>
              <SubTitle>Alignment</SubTitle>
            </Header>
            <AttributeList>
              {alignment ? (
                <FullCopyAttribute
                  value={
                    <TextAlignValue
                      alignment={alignment}
                      label="Horizontal Alignment"
                    />
                  }
                  copyValue={alignment}
                  label="Horizontal"
                  isTextStyle
                  dirtyAttributes={getDirtyAttributes({
                    originalValueKey: 'alignment',
                    valueToDisplay:
                      textOriginalStyles?.paragraphStyle?.alignment,
                    originalValues: textOriginalStyles?.paragraphStyle,
                    labelToDisplay: 'Horizontal Alignment',
                  })}
                />
              ) : null}
              {verticalAlignment ? (
                <FullCopyAttribute
                  value={
                    <TextAlignValue
                      alignment={verticalAlignment}
                      label="Vertical Alignment"
                    />
                  }
                  copyValue={verticalAlignment}
                  label="Vertical"
                  isTextStyle
                  dirtyAttributes={getDirtyAttributes({
                    originalValueKey: 'verticalAlignment',
                    valueToDisplay: textOriginalStyles?.verticalAlignment,
                    originalValues: textOriginalStyles,
                    labelToDisplay: 'Vertical Alignment',
                  })}
                />
              ) : null}
            </AttributeList>
          </>
        ) : null}
      </Section>
    </div>
  )
}

const attributesToArray = ({
  string,
  attributes,
  style,
  onColorFormatChange,
  colorFormat,
  colorVariables,
}: TextAttributedString & { style?: Style } & ColorFormatModifier & {
    colorVariables: { [key: string]: ColorVariable }
  }) => {
  let verticalAlignment:
    | TextAlignment.Top
    | TextAlignment.Middle
    | TextAlignment.Bottom
    | undefined = undefined
  if (style && style.text) {
    verticalAlignment = style.text.verticalAlignment
  }
  const result: Array<
    TextAttributes & {
      text: string
    } & ColorFormatModifier & {
        colorVariables: { [key: string]: ColorVariable }
      }
  > = attributes.map(a => ({
    ...a.attributes,
    text: string.slice(a.location, a.location + a.length),
    verticalAlignment,
    onColorFormatChange,
    colorFormat,
    colorVariables,
    originalSharedStyleValues: style?.originalSharedStyleValues as TextStyle,
  }))
  return result
}

type ColorFormatModifier = {
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
}

const isWhitespace = (string: string): boolean => {
  return /^\s+$/.test(string)
}

const Text = ({
  attributedString,
  style,
  onColorFormatChange,
  colorFormat,
  colorVariables,
}: TextProps &
  ColorFormatModifier & {
    colorVariables: { [key: string]: ColorVariable }
  }) => (
  <>
    {attributesToArray({
      ...attributedString,
      style,
      onColorFormatChange,
      colorFormat,
      colorVariables,
    })
      .filter(attribute => !isWhitespace(attribute.text))
      .map(renderTextAttribute)}
  </>
)

export default Text
